import { useState, useEffect, memo, type CSSProperties } from "react";
import { type ImageContentFit } from "expo-image";
import { SignedSnapshot, AllowedWidth } from "..";

export interface SnapshotImageProps {
  /** Signed snapshot data, or null/undefined if no snapshot available */
  snapshot: SignedSnapshot | null | undefined;
  width: AllowedWidth | null | undefined;
  /** Style for the image */
  style?: CSSProperties;
  /** How the image should fit within its container */
  contentFit?: ImageContentFit;
  /** Color for the placeholder when no snapshot is available */
  placeholderColor?: string;
}

/**
 * Web version - Image component that displays a signed snapshot with proper headers.
 * Uses native HTML elements to avoid react-native-web's duplicate React instance
 * in pnpm monorepos (expo-image also causes HMR issues on web).
 * Shows a placeholder when no snapshot is available.
 */
export const SnapshotImage = memo(function SnapshotImage({
  snapshot,
  width,
  style,
  contentFit = "cover",
  placeholderColor = "#e0e0e0",
}: SnapshotImageProps) {
  const [hasError, setHasError] = useState(false);
  const [imageSrc, setImageSrc] = useState<string | null>(null);

  // Reset error state and fetch new image when snapshot changes
  useEffect(() => {
    setHasError(false);

    if (!snapshot) {
      setImageSrc(null);
      return;
    }

    const controller = new AbortController();
    const url = snapshot.url + (width ? `?width=${width.toFixed()}` : "");

    fetch(url, { headers: snapshot.headers, signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${String(res.status)}`);
        return res.blob();
      })
      .then((blob) => {
        setImageSrc(URL.createObjectURL(blob));
      })
      .catch((err: unknown) => {
        if (err instanceof Error && err.name !== "AbortError") {
          setHasError(true);
        }
      });

    return () => {
      controller.abort();
    };
  }, [snapshot, width]);

  // Clean up object URL when component unmounts or image changes
  useEffect(() => {
    return () => {
      if (imageSrc) {
        URL.revokeObjectURL(imageSrc);
      }
    };
  }, [imageSrc]);

  const shouldShowImage = snapshot && imageSrc && !hasError;

  if (!shouldShowImage) {
    return <div style={{ width: "100%", height: "100%", backgroundColor: placeholderColor, ...style }} />;
  }

  return (
    <div style={{ width: "100%", height: "100%", ...style }}>
      <img
        src={imageSrc}
        style={{
          width: "100%",
          height: "100%",
          objectFit: contentFit === "none" ? "none" : contentFit,
        }}
        onError={() => setHasError(true)}
      />
    </div>
  );
});
